import { computed, ref, watch, type Ref } from 'vue'
import {
  accepted,
  escape,
  initialNav,
  moveSelection,
  reconcile,
  reopen,
  selectedIndex,
  typed,
} from '@/todos/suggestNav'
import { suggestHashtags } from '@/todos/tags'

export interface TagNameSuggestionSources {
  value: Ref<string> // (the whole field is the query, unlike a hashtag inside a text)
  focused: Ref<boolean>
  names: () => string[] // (the candidates)
  known: () => string[] // (the candidates in usage order, most used first)
}

// The tag completion of a plain tag field (the filter bar, the tag settings): the same ranking (`suggestHashtags`) and keyboard
// state (`todos/suggestNav.ts`) as `useHashtagCompletion`, but the field value as a whole is what is searched. An empty
// field lists the most used candidates. The field picks the name `accept` or `onTab` hand back.
export function useTagNameSuggestions(sources: TagNameSuggestionSources) {
  const nav = ref(initialNav)
  const matches = computed(() =>
    suggestHashtags(sources.names(), sources.known(), sources.value.value.replace(/^#+/, '')),
  )
  const matchNames = computed(() => matches.value.map((match) => match.name))
  const active = computed(() => selectedIndex(nav.value, matchNames.value))
  watch(matchNames, (shown) => {
    nav.value = reconcile(nav.value, shown)
  })
  const open = computed(
    () => sources.focused.value && !nav.value.closed && matches.value.length > 0,
  )

  function move(step: 1 | -1, event: Event): void {
    if (!open.value) {
      // (a list that Escape closed comes back with the arrow keys)
      if (nav.value.closed && matches.value.length) {
        event.preventDefault()
        nav.value = reopen()
      }
      return
    }
    event.preventDefault()
    nav.value = moveSelection(nav.value, matchNames.value, step)
  }

  // The name Enter takes: only while the list is open and something is selected.
  function accept(): string | null {
    return open.value ? accepted(nav.value, matchNames.value) : null
  }

  // Tab takes the selected entry too, and reopens a list that Escape closed; else (and with Shift) it keeps its normal job.
  function onTab(event: KeyboardEvent): string | null {
    if (event.shiftKey) return null
    const name = accept()
    if (name !== null) {
      event.preventDefault()
      return name
    }
    if (nav.value.closed && matches.value.length) {
      event.preventDefault()
      nav.value = reopen()
    }
    return null
  }

  // Escape closes the list and drops the selection; while it was open the key is not meant for anything around the field.
  function onEscape(event: Event): void {
    if (open.value) event.stopPropagation()
    nav.value = escape(nav.value)
  }

  return {
    matches,
    active,
    open,
    move,
    accept,
    onTab,
    onEscape,
    typed: (): void => {
      nav.value = typed(nav.value)
    },
    reset: (): void => {
      nav.value = initialNav
    },
  }
}
